// Diálogo "Crear panel": desde cero o desde una plantilla sugerida (estilo HubSpot).
import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Plus, LayoutDashboard, ChevronLeft, BarChart3 } from "lucide-react";
import { toast } from "sonner";
import { PANEL_TEMPLATES, VISIBILITY_OPTIONS } from "./panelTemplates";

export default function PanelTemplatesDialog({ pipelines, users, defaultPipelineId, onClose, onFromScratch, onCreate }) {
  const [tpl, setTpl] = useState(null);
  const [name, setName] = useState("");
  const [pipelineId, setPipelineId] = useState(defaultPipelineId || pipelines[0]?.id || "");
  const [tags, setTags] = useState("");
  const [visibility, setVisibility] = useState("private");
  const [allowedIds, setAllowedIds] = useState([]);
  const [checked, setChecked] = useState({});

  const pickTemplate = (t) => {
    setTpl(t);
    setName(t.name);
    setChecked(Object.fromEntries(t.reports.map((r) => [r.key, true])));
  };

  const toggleUser = (uid) => setAllowedIds((p) => p.includes(uid) ? p.filter((x) => x !== uid) : [...p, uid]);

  const handleCreate = () => {
    const reports = tpl.reports.filter((r) => checked[r.key]);
    if (!name.trim()) { toast.error("El nombre del panel es obligatorio"); return; }
    if (!pipelineId) { toast.error("Seleccioná el pipeline de origen de los datos"); return; }
    if (!reports.length) { toast.error("Seleccioná al menos un informe"); return; }
    onCreate({
      name: name.trim(),
      description: tpl.description,
      pipeline_id: pipelineId,
      tags: tags.split(",").map((t) => t.trim()).filter(Boolean),
      visibility,
      allowed_user_ids: visibility === "custom" ? allowedIds : [],
      is_shared: visibility === "shared_view" || visibility === "shared_edit",
      widgets: reports.map((r, i) => ({
        id: `w_${Date.now()}_${i}`,
        type: r.type, title: r.title, pipeline_id: pipelineId,
        metric: r.metric, group_by_field: r.group_by_field || null,
        filters: {}, x: 0, y: 0, w: r.w || 6, h: r.h || 4, color: "#193966",
      })),
    });
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="panel-templates-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {tpl && (
              <button onClick={() => setTpl(null)} className="p-1 hover:bg-slate-100 rounded" data-testid="template-back-btn">
                <ChevronLeft className="w-4 h-4" />
              </button>
            )}
            {tpl ? `Plantilla: ${tpl.name}` : "Crear panel"}
          </DialogTitle>
        </DialogHeader>

        {!tpl ? (
          <div className="space-y-3">
            <button
              onClick={onFromScratch}
              className="w-full border-2 border-dashed border-[#80BC00]/60 rounded-lg p-4 text-left hover:bg-[#80BC00]/5 transition-colors flex items-center gap-3"
              data-testid="template-from-scratch"
            >
              <div className="w-9 h-9 rounded-lg bg-[#80BC00]/15 text-[#5f8f00] flex items-center justify-center flex-shrink-0"><Plus className="w-5 h-5" /></div>
              <div>
                <p className="text-sm font-bold text-slate-800">Nuevo panel desde cero</p>
                <p className="text-xs text-slate-500">Armá tus widgets uno por uno con el editor completo.</p>
              </div>
            </button>
            <p className="text-xs uppercase tracking-wider font-bold text-slate-500">Plantillas sugeridas</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {PANEL_TEMPLATES.map((t) => (
                <button
                  key={t.id}
                  onClick={() => pickTemplate(t)}
                  className="border border-slate-200 rounded-lg p-3 text-left hover:border-[#193966]/50 hover:shadow-sm transition-all"
                  data-testid={`template-card-${t.id}`}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <LayoutDashboard className="w-4 h-4 text-[#193966]" />
                    <p className="text-sm font-bold text-slate-800">{t.name}</p>
                  </div>
                  <p className="text-xs text-slate-500 line-clamp-2">{t.description}</p>
                  <Badge variant="outline" className="mt-2 text-[10px] border-[#80BC00]/50 text-[#5f8f00]">
                    <BarChart3 className="w-3 h-3 mr-1" /> {t.reports.length} informes incluidos
                  </Badge>
                </button>
              ))}
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <p className="text-xs text-slate-600">{tpl.description}</p>
            <p className="text-[11px] text-slate-500"><b>¿A quién le sirve?</b> {tpl.audience}</p>
            <div>
              <Label className="text-xs">Informes incluidos en este panel</Label>
              <div className="border rounded p-2 bg-slate-50 space-y-1 mt-1">
                {tpl.reports.map((r) => (
                  <label key={r.key} className="flex items-center gap-2 text-xs cursor-pointer">
                    <input
                      type="checkbox"
                      checked={!!checked[r.key]}
                      onChange={(e) => setChecked({ ...checked, [r.key]: e.target.checked })}
                      className="w-3.5 h-3.5 accent-[#193966]"
                      data-testid={`template-report-${r.key}`}
                    /> {r.title}
                  </label>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Nombre del panel <span className="text-rose-500">*</span></Label>
                <Input value={name} onChange={(e) => setName(e.target.value)} data-testid="template-panel-name" />
              </div>
              <div>
                <Label className="text-xs">Pipeline de origen <span className="text-rose-500">*</span></Label>
                <select value={pipelineId} onChange={(e) => setPipelineId(e.target.value)} className="w-full h-9 border border-slate-300 rounded px-2 text-sm bg-white" data-testid="template-pipeline">
                  {pipelines.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </div>
              <div>
                <Label className="text-xs">Etiquetas (separadas por coma)</Label>
                <Input value={tags} onChange={(e) => setTags(e.target.value)} placeholder="ventas, mensual" data-testid="template-tags" />
              </div>
              <div>
                <Label className="text-xs">¿Quiénes pueden acceder?</Label>
                <select value={visibility} onChange={(e) => setVisibility(e.target.value)} className="w-full h-9 border border-slate-300 rounded px-2 text-sm bg-white" data-testid="template-visibility">
                  {VISIBILITY_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
            </div>
            {visibility === "custom" && (
              <div>
                <Label className="text-xs">Usuarios con acceso</Label>
                <div className="border rounded p-2 bg-slate-50 max-h-40 overflow-y-auto grid grid-cols-2 gap-1 mt-1">
                  {(users || []).map((u) => (
                    <label key={u.id} className="flex items-center gap-1.5 text-xs cursor-pointer">
                      <input type="checkbox" checked={allowedIds.includes(u.id)} onChange={() => toggleUser(u.id)} className="w-3.5 h-3.5 accent-[#193966]" data-testid={`template-user-${u.id}`} />
                      <span className="truncate">{u.name || u.email}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
            <DialogFooter className="pt-2">
              <Button variant="outline" onClick={() => setTpl(null)}>Anterior</Button>
              <Button onClick={handleCreate} className="bg-[#80BC00] hover:bg-[#6ea700] text-white" data-testid="template-create-btn">
                <Plus className="w-4 h-4 mr-1.5" /> Crear panel
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
